/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, GraduationCap, CheckCircle, Clock, PlayCircle, RefreshCw, BarChart2, Eye } from "lucide-react";
import apiService from "services/apiService";
import { useToaster } from "contexts/toasterContext";
import { formatTime } from "../sharedUtils";

interface TrainingProgress {
    trainingId: number;
    trainingName: string;
    totalExercises: number;
    attempted: number;
    correct: number;
    averageScore: number;
    status: "ASSIGNED" | "IN_PROGRESS" | "COMPLETED";
    assignedAt: string;
    startedAt: string | null;
    finishedAt: string | null;
}

interface MyProgressSummary {
    userId: number;
    userName: string;
    totalAssigned: number;
    totalCompleted: number;
    overallAverageScore: number;
    trainings: TrainingProgress[];
}

export default function UserTrainingPage() {
    const { addToast } = useToaster();
    const navigate = useNavigate();

    const [progressData, setProgressData] = useState<MyProgressSummary | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchMyProgress = async () => {
            try {
                const res = await apiService.get("api/gp/progress/me");
                setProgressData(res.data);
            } catch (error: any) {
                addToast(error?.response?.data?.error || "Failed to load your trainings.", "error");
            } finally {
                setLoading(false);
            }
        };
        fetchMyProgress();
    }, [addToast]);

    const handleStartTraining = (id: number) => {
        // Navigate to the execution page
        navigate(`/admin/training/${id}/execute`);
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-[calc(100vh-100px)]">
                <RefreshCw className="animate-spin text-blue-500 mb-4" size={32} />
                <p className="text-gray-500 font-medium">Loading your trainings...</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full min-h-[calc(100vh-100px)] bg-gray-50 dark:bg-gray-900 rounded-xl overflow-hidden transition-colors duration-300 border border-gray-200 dark:border-gray-800">

            {/* HEADER */}
            <div className="px-6 py-6 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 shrink-0 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                    <GraduationCap className="text-blue-600 dark:text-blue-400" size={28} />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Learning Dashboard</h2>
                </div>

                {/* SUMMARY CARDS */}
                {progressData && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-800/50 flex items-center gap-4">
                            <div className="p-3 bg-blue-100 dark:bg-blue-800/50 rounded-lg text-blue-600 dark:text-blue-400"><BookOpen size={24} /></div>
                            <div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Assigned</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{progressData.totalAssigned}</p>
                            </div>
                        </div>
                        <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-xl border border-green-100 dark:border-green-800/50 flex items-center gap-4">
                            <div className="p-3 bg-green-100 dark:bg-green-800/50 rounded-lg text-green-600 dark:text-green-400"><CheckCircle size={24} /></div>
                            <div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Completed</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{progressData.totalCompleted}</p>
                            </div>
                        </div>
                        {/* <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-xl border border-purple-100 dark:border-purple-800/50 flex items-center gap-4">
                            <div className="p-3 bg-purple-100 dark:bg-purple-800/50 rounded-lg text-purple-600 dark:text-purple-400"><BarChart2 size={24} /></div>
                            <div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Average Score</p>
                                <p className="text-2xl font-bold text-gray-900 dark:text-white">{Math.round(progressData.overallAverageScore * 100)}%</p>
                            </div>
                        </div> */}
                    </div>
                )}
            </div>

            {/* TRAINING LIST */}
            <div className="flex-1 overflow-y-auto p-6 lg:p-8 ">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Assigned Trainings</h3>

                {!progressData?.trainings || progressData.trainings.length === 0 ? (
                    <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                        <BookOpen size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                        <p className="text-gray-500 dark:text-gray-400 font-medium">You currently have no assigned trainings.</p>
                    </div>
                ) : (
                        <div className="grid gap-5 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
                        {progressData.trainings.map((training) => {
                            const progressPercent = training.totalExercises > 0 ? Math.round((training.attempted / training.totalExercises) * 100) : 0;

                            return (
                                <div key={training.trainingId} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-all flex flex-col h-full overflow-hidden">
                                    <div className="p-4 flex-1 flex flex-col">
                                        <div className="flex justify-between items-start mb-3">
                                            <h4 className="font-semibold text-base text-gray-900 dark:text-white leading-snug break-words">{training.trainingName}</h4>
                                            <span className={`px-2 py-0.5 text-[9px] font-bold tracking-wider uppercase rounded-md shrink-0 ${training.status === 'COMPLETED' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                                    training.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                                        'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                                }`}>
                                                {training.status.replace('_', ' ')}
                                            </span>
                                        </div>

                                        <div className="space-y-1.5 mt-3 text-sm text-gray-600 dark:text-gray-400">
                                            <div className="flex items-center gap-2">
                                                <Clock size={16} className="text-gray-400 shrink-0" />
                                                <span>
                                                    <span className="font-medium">Assigned:</span>{" "}
                                                    {formatTime(training.assignedAt)}
                                                </span>
                                            </div>

                                            {training.startedAt && (
                                                <div className="flex items-center gap-2">
                                                    <PlayCircle size={16} className="text-blue-500 shrink-0" />
                                                    <span>
                                                        <span className="font-medium">Started:</span>{" "}
                                                        {formatTime(training.startedAt)}
                                                    </span>
                                                </div>
                                            )}

                                            {training.finishedAt && (
                                                <div className="flex items-center gap-2">
                                                    <CheckCircle size={16} className="text-green-500 shrink-0" />
                                                    <span>
                                                        <span className="font-medium">Finished:</span>{" "}
                                                        {formatTime(training.finishedAt)}
                                                    </span>
                                                </div>
                                            )}

                                            {training.status === "COMPLETED" && (
                                                <div className="flex items-center gap-2">
                                                    <BarChart2 size={16} className="text-purple-500 shrink-0" />
                                                    <span>
                                                        <span className="font-medium">Score:</span>{" "}
                                                        <span className="font-bold text-gray-900 dark:text-white">
                                                            {Math.round(training.averageScore * 100)}%
                                                        </span>
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="mt-4">
                                            <div className="flex justify-between text-xs font-medium mb-1">
                                                <span className="text-gray-600 dark:text-gray-400">Progress</span>
                                                <span className="text-gray-900 dark:text-white">{training.attempted} / {training.totalExercises} Completed</span>
                                            </div>
                                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                                <div
                                                    className={`h-2 rounded-full transition-all duration-500 ${training.status === 'COMPLETED' ? 'bg-green-500' : 'bg-blue-600'}`}
                                                    style={{ width: `${progressPercent}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Action Button */}
                                    <div className="p-3 bg-gray-50 dark:bg-gray-800/80 border-t border-gray-100 dark:border-gray-700">
                                        <button
                                            onClick={() => handleStartTraining(training.trainingId)}
                                            className={`w-full h-10 flex items-center justify-center gap-2 rounded-lg text-sm font-medium transition-colors ${training.status === 'COMPLETED'
                                                    ? 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
                                                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
                                                }`}
                                        >
                                            {training.status === 'COMPLETED' ? <Eye size={16} /> : <PlayCircle size={16} />}
                                            {training.status === 'ASSIGNED' ? 'Start Training' : training.status === 'IN_PROGRESS' ? 'Resume Training' : 'Review Results'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}